import React from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { ContentPreview } from '@/components/ui/content-preview'
import { Star } from 'lucide-react'
import type { Service } from '@/features/services/types'
import { buildServicePreviewMetadata } from '@/features/services/utils'

interface ServicesPreviewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  service: Service | null
}

export function ServicesPreviewDialog({ open, onOpenChange, service }: ServicesPreviewDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{service?.title || 'Service Preview'}</DialogTitle>
        </DialogHeader>
        {service && (() => {
          const metadata = buildServicePreviewMetadata(service)
          return (
            <div className="space-y-6">
              <div className="border-b pb-6">
                {metadata.heroImage && (
                  <div className="aspect-video rounded-lg overflow-hidden mb-4">
                    <img src={metadata.heroImage} alt={service.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="space-y-4">
                  {metadata.heroHeadline && (
                    <h2 className="text-2xl font-bold text-blue-600">{metadata.heroHeadline}</h2>
                  )}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                    {metadata.badges.map((badge) => (
                      <Badge key={badge.label} variant={badge.variant} className={badge.className}>
                        {badge.icon === 'star' && <Star className="w-3 h-3 mr-1" />}
                        {badge.label}
                      </Badge>
                    ))}
                  </div>
                  {metadata.description && <p className="text-lg text-muted-foreground">{metadata.description}</p>}
                </div>
              </div>

                <div className="space-y-6">
                  <ContentPreview
                    content={service.content || service.full_description || ''}
                    showBorder={false}
                    maxHeight="none"
                    className="prose-lg"
                  />
                {metadata.benefits.length > 0 && (
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Service Benefits</h3>
                    <div className="grid gap-4 md:grid-cols-2">
                      {metadata.benefits.map((benefit) => (
                        <div key={benefit.id} className="p-4 border rounded-lg">
                          <h4 className="font-medium mb-2">{benefit.title}</h4>
                          <p className="text-sm text-muted-foreground">{benefit.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {metadata.processSteps.length > 0 && (
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Our Process</h3>
                    <div className="space-y-4">
                      {metadata.processSteps.map((step) => (
                        <div key={step.id} className="flex gap-4">
                          <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                            {step.step_number}
                          </div>
                          <div>
                            <h4 className="font-medium">{step.title}</h4>
                            <p className="text-sm text-muted-foreground">{step.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {metadata.faqs.length > 0 && (
                  <div>
                    <h3 className="text-xl font-semibold mb-4">FAQs</h3>
                    <div className="space-y-4">
                      {metadata.faqs.map((faq) => (
                        <div key={faq.id}>
                          <h4 className="font-medium">{faq.question}</h4>
                          <p className="text-sm text-muted-foreground">{faq.answer}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        })()}
      </DialogContent>
    </Dialog>
  )
}
